import {
  makeDeleteApiCall,
  makeGetApiCall,
  makePatchApiCall,
  makePostApiCall,
} from "./makeApiCall";
import type { ApiMessage } from "../types/api";
import type { WarehouseLocationOption, WarehouseOption } from "../types/inventory";
import type {
  ReceivingInvoice,
  ReceivingInvoiceItem,
  ReceivingInvoicePage,
  ReceivingInvoicePayload,
  ReceivingItemPayload,
  ReceivingProductOption,
  ReceivingStatus,
  Supplier,
  SupplierOption,
  SupplierPage,
  SupplierPayload,
  Warehouse,
  WarehouseLocation,
  WarehouseLocationPayload,
  WarehousePayload,
} from "../types/receiving";

export const receivingApi = {
  invoices: (search = "", status: ReceivingStatus | "" = "") =>
    makeGetApiCall<ReceivingInvoicePage>(
      `/receiving-invoices?search=${encodeURIComponent(search)}&status=${encodeURIComponent(status)}`,
    ),
  invoice: (id: number | string) => makeGetApiCall<ReceivingInvoice>(`/receiving-invoices/${id}`),
  createInvoice: (payload: ReceivingInvoicePayload) =>
    makePostApiCall<ReceivingInvoice, ReceivingInvoicePayload>("/receiving-invoices", payload),
  updateInvoice: (id: number | string, payload: Partial<ReceivingInvoicePayload>) =>
    makePatchApiCall<ReceivingInvoice, Partial<ReceivingInvoicePayload>>(`/receiving-invoices/${id}`, payload),
  completeInvoice: (id: number | string) => makePostApiCall<ReceivingInvoice>(`/receiving-invoices/${id}/complete`, {}),
  voidInvoice: (id: number | string) => makePostApiCall<ReceivingInvoice>(`/receiving-invoices/${id}/void`, {}),
  updateStatus: (id: number | string, status: ReceivingStatus) =>
    makePostApiCall<ReceivingInvoice, { status: ReceivingStatus }>(`/receiving-invoices/${id}/status`, { status }),
  addItem: (invoiceId: number | string, payload: ReceivingItemPayload) =>
    makePostApiCall<ReceivingInvoiceItem, ReceivingItemPayload>(`/receiving-invoices/${invoiceId}/items`, payload),
  updateItem: (invoiceId: number | string, itemId: number | string, payload: ReceivingItemPayload) =>
    makePatchApiCall<ReceivingInvoiceItem, ReceivingItemPayload>(`/receiving-invoices/${invoiceId}/items/${itemId}`, payload),
  deleteItem: (invoiceId: number | string, itemId: number | string) =>
    makeDeleteApiCall<ApiMessage>(`/receiving-invoices/${invoiceId}/items/${itemId}`),
  suppliers: (search = "") => makeGetApiCall<SupplierOption[]>(`/suppliers/options?search=${encodeURIComponent(search)}`),
  supplierList: (search = "") => makeGetApiCall<SupplierPage>(`/suppliers?search=${encodeURIComponent(search)}`),
  createSupplier: (payload: SupplierPayload) => makePostApiCall<Supplier, SupplierPayload>("/suppliers", payload),
  updateSupplier: (id: number | string, payload: Partial<SupplierPayload>) =>
    makePatchApiCall<Supplier, Partial<SupplierPayload>>(`/suppliers/${id}`, payload),
  warehouses: () => makeGetApiCall<Warehouse[]>("/warehouses"),
  warehouseOptions: () => makeGetApiCall<WarehouseOption[]>("/warehouses/options"),
  locations: (warehouseId: number | string) => makeGetApiCall<WarehouseLocationOption[]>(`/warehouses/${warehouseId}/locations`),
  createWarehouse: (payload: WarehousePayload) => makePostApiCall<Warehouse, WarehousePayload>("/warehouses", payload),
  updateWarehouse: (id: number | string, payload: Partial<WarehousePayload>) =>
    makePatchApiCall<Warehouse, Partial<WarehousePayload>>(`/warehouses/${id}`, payload),
  createLocation: (warehouseId: number | string, payload: WarehouseLocationPayload) =>
    makePostApiCall<WarehouseLocation, WarehouseLocationPayload>(`/warehouses/${warehouseId}/locations`, payload),
  updateLocation: (id: number | string, payload: Partial<WarehouseLocationPayload>) =>
    makePatchApiCall<WarehouseLocation, Partial<WarehouseLocationPayload>>(`/warehouse-locations/${id}`, payload),
  products: (search: string) => makePostApiCall<ReceivingProductOption[], { search: string }>("/fetchProductsForDropdown", { search }),
};
